import { Navigate, Route, useLocation } from "react-router-dom";
import {
  goalToParam,
  STEPS,
  SUB_FLOW_ORDER,
  SUB_FLOW_STEP_ID,
  ONBOARDING_BASE_PATH,
  furthestReachableStep,
} from "./steps/registry";
import { FlowProvider, useFlowState } from "./state/FlowProvider";
import { initialFlowState } from "./state/flowReducer";
import OnboardingLayout from "./OnboardingLayout";
import StepGuard from "./StepGuard";
import StepPlaceholder from "./components/StepPlaceholder";
import WelcomeStep from "./steps/screens/WelcomeStep";
import DobStep from "./steps/screens/DobStep";
import CreateAccountStep from "./steps/screens/CreateAccountStep";
import VerifyEmailStep from "./steps/screens/VerifyEmailStep";
import GoalsStep from "./steps/screens/GoalsStep";
import GoalSubflowIndex from "./steps/screens/GoalSubflow/GoalSubflowIndex";
import ReceiveMentorship from "./steps/screens/GoalSubflow/ReceiveMentorship";
import ProvideMentorship from "./steps/screens/GoalSubflow/ProvideMentorship";
import Networking from "./steps/screens/GoalSubflow/Networking";
import GrowCareer from "./steps/screens/GoalSubflow/GrowCareer";
import BuildSkills from "./steps/screens/GoalSubflow/BuildSkills";
import ProfileStep from "./steps/screens/ProfileStep";
import DemographicsStep from "./steps/screens/DemographicsStep";
import DoneStep from "./steps/screens/DoneStep";
import { isSignupDeepLink } from "../../router/signupDeepLink";

/**
 * Onboarding routes, generated from the step registry.
 *
 * Nothing here enumerates the flow. Adding a step is an edit to `registry.js`
 * and an entry in `SCREENS` below — the route, the guard, the progress
 * denominator and back-navigation all follow from it. A hand-written route
 * list would be a second place the sequence lives, and the two would disagree
 * the first time anyone reordered a step.
 *
 * Public, not behind `ProtectedRoute`: a visitor has no account until the
 * account-creation step, so requiring a session to reach the flow that creates
 * one would lock everyone out.
 */

/**
 * Screens, keyed by step id.
 *
 * A step with no entry renders `StepPlaceholder`, which is what lets the whole
 * flow be walked and tested before the screens exist. The registry is kept free
 * of JSX so it stays a plain, easily-tested module — this is where components
 * attach.
 *
 * ECH-353 fills welcome and dob; ECH-350 create_account and verify_email;
 * ECH-116 goals; ECH-354 profile, demographics and done. `person_flow` has no
 * screen of its own — its five sub-flows are routed below.
 */
const SCREENS = {
  welcome: WelcomeStep,
  dob: DobStep,
  create_account: CreateAccountStep,
  verify_email: VerifyEmailStep,
  goals: GoalsStep,
  profile: ProfileStep,
  demographics: DemographicsStep,
  done: DoneStep,
};

const screenFor = (step) => {
  const Screen = SCREENS[step.id];
  return Screen ? <Screen step={step} /> : <StepPlaceholder step={step} />;
};

/**
 * Goal sub-flow screens, keyed by goal id.
 *
 * A second map because the five sub-flows are one step: `SCREENS` above is
 * keyed by step id and has no room to tell them apart.
 *
 * Every goal in `SUB_FLOW_ORDER` must appear here — a missing entry renders an
 * undefined component. `routing.test.jsx` asserts the two agree, which is the
 * cheaper way to find out.
 */
export const SUB_FLOWS = {
  receive_mentorship: ReceiveMentorship,
  provide_mentorship: ProvideMentorship,
  networking: Networking,
  grow_career: GrowCareer,
  build_skills: BuildSkills,
};

const subFlowScreenFor = (goal) => {
  const Screen = SUB_FLOWS[goal];
  return <Screen />;
};

/**
 * The five goal sub-flows, nested under the one step they share.
 *
 * Five routes, one step. `person_flow` stays a single entry in `STEPS`, so the
 * progress denominator is fixed once the pathway is known and does not grow
 * with goals selected — which ECH-116 requires. `getStepByLocation` matches the
 * first segment after the base, so everything under `about-you/` still resolves
 * to the step, and the chrome, the guard and back-navigation keep working
 * without a reverse map from sub-flow to step.
 *
 * Generated from `SUB_FLOW_ORDER` for the same reason the steps are generated
 * from `STEPS`: reordering the sub-flows must not mean editing a route list
 * that would then disagree with the registry.
 */
const subFlowRoutes = (step) => (
  <Route key={step.id} path={step.path}>
    <Route
      index
      element={
        <StepGuard step={step}>
          <GoalSubflowIndex step={step} />
        </StepGuard>
      }
    />

    {SUB_FLOW_ORDER.map((goal) => (
      <Route
        key={goal}
        path={goalToParam(goal)}
        element={
          <StepGuard step={step} goal={goal}>
            {subFlowScreenFor(goal)}
          </StepGuard>
        }
      />
    ))}

    {/* An unknown sub-flow returns to the step, not to the start of the flow.
        Without this the onboarding-wide catch-all below would match it and drop
        the member back at welcome. Absolute, because a relative target inside a
        splat resolves against the splat and loops. */}
    <Route
      path="*"
      element={<Navigate to={`${ONBOARDING_BASE_PATH}/${step.path}`} replace />}
    />
  </Route>
);

export { ONBOARDING_BASE_PATH };

/**
 * Absolute, deliberately.
 *
 * A relative `<Navigate to="welcome">` resolves against the matched path, and
 * inside a splat route that path includes the splat — so an unknown step
 * redirects to `/onboarding/<unknown>/welcome`, which matches the splat again,
 * and again. The loop is synchronous and tight enough to hang the tab rather
 * than error. An absolute path cannot express that mistake.
 */
const firstStepPath = `${ONBOARDING_BASE_PATH}/${STEPS[0].path}`;

/**
 * Rendered inside the app's top-level `<Routes>`.
 *
 * `FlowProvider` wraps the layout rather than the whole app so flow state is
 * created and destroyed with the route, and nothing outside onboarding can
 * reach it.
 */
/**
 * Keeps a signed-in member out of the signup flow.
 *
 * Once signup routes here (ECH-375), a member who already has an account can
 * reach it by clicking "Sign Up", following an old bookmark, or opening a
 * shared `?signup=1` link. Starting a second onboarding on a finished account
 * ends at a registration the server refuses, on a step they cannot pass, having
 * lost their place in the app.
 *
 * Guarded at the flow root rather than on the link, so every entry point is
 * covered at once — including a typed URL and the mobile in-app browser.
 *
 * `localStorage.token` is the app session specifically. The onboarding session
 * lives in sessionStorage until completion, so somebody genuinely mid-flow is
 * not caught by this. Done installs the app session on arrival and stays on its
 * URL, so this next runs on a later navigation or a reload, both of which
 * belong on the dashboard.
 */
const RequireNoAppSession = ({ children }) => {
  if (localStorage.getItem("token")) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

/** Resume a persisted journey instead of sending a returning member to welcome. */
const OnboardingEntry = () => {
  const state = useFlowState();
  const step = furthestReachableStep(state);
  return <Navigate to={`${ONBOARDING_BASE_PATH}/${step.path}`} replace />;
};

const OnboardingFlow = () => {
  const { search } = useLocation();
  const startsNewSignup = isSignupDeepLink(search);

  return (
    <FlowProvider initialState={startsNewSignup ? initialFlowState : undefined}>
      <OnboardingLayout />
    </FlowProvider>
  );
};

export const onboardingRoute = (
  <Route
    key="onboarding"
    path={ONBOARDING_BASE_PATH}
    element={
      <RequireNoAppSession>
        <OnboardingFlow />
      </RequireNoAppSession>
    }
  >
    <Route index element={<OnboardingEntry />} />

    {STEPS.map((step) =>
      step.id === SUB_FLOW_STEP_ID ? (
        subFlowRoutes(step)
      ) : (
        <Route
          key={step.id}
          path={step.path}
          element={<StepGuard step={step}>{screenFor(step)}</StepGuard>}
        />
      ),
    )}

    {/* An unknown step path returns to the start rather than rendering the
        app-wide 404, which would drop the member out of the flow entirely. */}
    <Route path="*" element={<Navigate to={firstStepPath} replace />} />
  </Route>
);
